/**
 * `More actions ▸ Duplicate board` (spec M1-B1 §7.5).
 *
 * The three `DuplicateBoardType` values phrased as monday phrases them, a destination
 * (defaulting to the source board's, which is what the api does when the fields are omitted
 * `[sdl]`) and **Keep subscribers**, off by default. Above 500 items the service answers
 * `is_async: true` and the copy finishes in the worker — the dialog says so rather than
 * pretending the board is ready (B9).
 */
import { useState } from 'react';
import { useToast, useOverlayDismiss } from '@elizmonday/ui';
import { useNavigate } from 'react-router';
import { boardUrl } from '../shell/urls';
import { useBoard, useBoardMutations } from './hooks';

const TYPES = [
  ['duplicate_board_with_structure', 'Structure only', 'Columns, groups and views. No items.'],
  ['duplicate_board_with_pulses', 'Structure and items', 'Everything above, plus every item and its values.'],
  [
    'duplicate_board_with_pulses_and_updates',
    'Structure, items and updates',
    'Everything above, plus the updates, replies and likes.',
  ],
] as const;

export function DuplicateBoardDialog({ boardId, onClose }: { boardId: string; onClose(): void }) {
  /** P-260: Escape closes it, and focus returns to the menu row that opened it. */
  useOverlayDismiss({ onClose });

  const board = useBoard(boardId);
  // Seeded once from the loaded board, like the terminology dialog: a dialog opened while
  // `shell.board` is still in flight would default its name to "Duplicate of ".
  if (!board.data) return null;
  return <Form boardId={boardId} boardName={board.data.name} onClose={onClose} />;
}

function Form({
  boardId,
  boardName,
  onClose,
}: {
  boardId: string;
  boardName: string;
  onClose(): void;
}) {
  const mutations = useBoardMutations();
  const navigate = useNavigate();
  const toast = useToast();
  const [type, setType] = useState<(typeof TYPES)[number][0]>('duplicate_board_with_pulses');
  const [name, setName] = useState(`Duplicate of ${boardName}`);
  const [keepSubscribers, setKeepSubscribers] = useState(false);

  return (
    <div className="ez-dialog-backdrop" role="presentation" onMouseDown={onClose}>
      <form
        className="ez-board-dialog"
        role="dialog"
        aria-modal="true"
        aria-label="Duplicate board"
        data-testid="duplicate-dialog"
        onMouseDown={(event) => event.stopPropagation()}
        onSubmit={(event) => {
          event.preventDefault();
          void (async () => {
            const result = await mutations.duplicate.mutateAsync({
              id: boardId,
              duplicateType: type,
              name: name.trim() || undefined,
              keepSubscribers,
            });
            onClose();
            if (result.isAsync) {
              toast('The copy is running in the background; it will appear in the sidebar shortly.');
              return;
            }
            toast(`${result.board.name} created`, {
              kind: 'positive',
              action: { label: 'Open', onClick: () => navigate(boardUrl(String(result.board.id))) },
            });
          })();
        }}
      >
        <h2 className="ez-board-dialog__title">Duplicate board</h2>

        <label className="ez-board-dialog__field">
          Name
          <input
            data-testid="duplicate-name"
            value={name}
            maxLength={255}
            onChange={(event) => setName(event.target.value)}
          />
        </label>

        <fieldset className="ez-board-dialog__radios">
          <legend>What to copy</legend>
          {TYPES.map(([value, label, hint]) => (
            <label key={value} className="ez-board-dialog__radio">
              <input
                type="radio"
                name="duplicate-type"
                value={value}
                checked={type === value}
                data-testid={`duplicate-${value}`}
                onChange={() => setType(value)}
              />
              <span>
                <strong>{label}</strong>
                <em>{hint}</em>
              </span>
            </label>
          ))}
        </fieldset>

        <label className="ez-board-dialog__field">
          <input
            type="checkbox"
            checked={keepSubscribers}
            data-testid="duplicate-keep-subscribers"
            onChange={(event) => setKeepSubscribers(event.target.checked)}
          />
          Keep subscribers
        </label>

        {/* E5: the conservative reading, and the person is told rather than surprised. */}
        <p className="ez-board-dialog__hint">
          Connected-board, mirror and dependency columns keep pointing at the original boards.
        </p>

        <footer className="ez-board-dialog__footer">
          <button type="button" className="ez-board-dialog__cancel" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="ez-board-dialog__save" data-testid="duplicate-submit">
            Duplicate
          </button>
        </footer>
      </form>
    </div>
  );
}
